import type {
  HomeRulesDef,
  InventoryRulesDef,
  ItemGroupDef,
  LoadoutRulesDef,
  LoadoutSlotDef,
} from './types';
import { skillRulesDefaults } from '../skills/defaults';
import { HOME_WIND_DEFAULTS } from '../wind/defaults';

/** Backgrounds moved to lib/skills (ADR 0066); re-exported for the existing imports. */
export { backgroundDefaults } from '../skills/defaults';

/*
 * The C# initializers of shared-cs HomeRulesDef.cs and InventoryDefs.cs, so a draft without a field shows (and validates
 * as) what the server's parser fills in. Lists start empty, like the C# (Newtonsoft appends into a non-empty default).
 */

// WIND fields (contract 3.7): lib/wind/defaults.ts.
export const homeRulesDefaults = (): HomeRulesDef => ({
  beds: { admitBelowHealthFrac: 0.8, minDurationMult: 0.25 },
  buildMarkMinSeconds: 900,
  dischargeMarkMinHours: 4,
  dischargeMarkMinHeal: 30,
  dischargeMarkMinRest: HOME_WIND_DEFAULTS.dischargeMarkMinRest,
  homeMarksPerDay: 2,
  headline: {
    badlyHurtHealthFrac: 0.3,
    badlyHurtTier: HOME_WIND_DEFAULTS.badlyHurtTier,
    safeCapDays: 14,
  },
  supplies: {
    lowDays: 2,
    shortDays: 1,
    minBandages: 2,
    bandageItemIds: [],
    storageAlmostFullFrac: 0.9,
  },
  onTableMax: 4,
  logMax: 30,
});

export const itemGroupDefaults = (id = ''): ItemGroupDef => ({
  id,
  name: '',
  order: 0,
  match: { survival: false, healAbove0: false, tags: [], categories: [], itemIds: [] },
});

/**
 * What an absent `inventoryRules` means: every limit enforced (the legacy behaviour; shared-cs `InventoryLimits` for a null block).
 * Applying these keeps that behaviour. A present block's own C# defaults are all limits off (`inventoryRulesBlockDefaults`).
 */
export const inventoryRulesAbsent = (): Required<InventoryRulesDef> => ({
  carryLimitsEnabled: true,
  storageLimitsEnabled: true,
  loadoutLimitsEnabled: true,
  fieldKitUsesHaul: true,
  raidKitHeldOnly: false,
});

/** shared-cs `InventoryRulesDef` initializers (a present block's missing fields). */
export const inventoryRulesBlockDefaults = (): Required<InventoryRulesDef> => ({
  carryLimitsEnabled: false,
  storageLimitsEnabled: false,
  loadoutLimitsEnabled: false,
  fieldKitUsesHaul: true,
  raidKitHeldOnly: false,
});

/** shared-cs `LoadoutSlotRules.DefaultSlots`: the built-in four when `loadoutRules` is absent or names no slots. */
export const defaultLoadoutSlots = (): LoadoutSlotDef[] => [
  { id: 'weapon1', label: 'Weapon 1', accepts: { categories: ['weapon'], tags: [] } },
  { id: 'weapon2', label: 'Weapon 2', accepts: { categories: ['weapon'], tags: [] } },
  { id: 'armor', label: 'Armor', accepts: { categories: ['armor'], tags: [] } },
  { id: 'backpack', label: 'Backpack', accepts: { categories: ['backpack'], tags: [] } },
];

/** What an absent `loadoutRules` plays as: the built-in slots, no goods limit. */
export const loadoutRulesDefaults = (): LoadoutRulesDef => ({
  slots: defaultLoadoutSlots(),
  maxGoods: 0,
});

/** The new `bases` fields (shared-cs `BaseRulesDef`, ADR 0049 and 0048). */
export const basesHbDefaults = () => ({
  caravansEnabled: true,
  autoHomeEnabled: true,
  autoHomeWindowDays: 14,
  autoHomeLeadDays: 3,
  autoHomeMinUseDays: 5,
  autoHomeFlagQuietHours: 24,
  autoDropSettleFixes: 3,
  autoDropWindowFixes: 8,
  autoDropSettleSeconds: 6,
  autoDropMaxAccuracyM: 50,
  autoDropMaxFixAgeSeconds: 30,
  autoDropBackoffSeconds: 20,
  autoDropBackoffMaxSeconds: 180,
});

/** The new `baseModules[].levels[]` bed fields (shared-cs `BaseModuleLevelDef`). */
export const moduleLevelBedDefaults = () => ({ beds: 0, bedDurationMult: 1, bedHealPerDay: 0 });

/** The homebase sections for the section editor (merged into `teamsSectionDefaults`), shown for a draft without them. */
export const hbSectionDefaults = {
  skillRules: skillRulesDefaults,
  homeRules: homeRulesDefaults,
  inventoryRules: inventoryRulesAbsent,
  loadoutRules: loadoutRulesDefaults,
} as const;
